import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { measureUnitService } from '../../services/measureUnitService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import MeasureUnitModal from '../../components/Setup/MeasureUnitModal';
import { PlusIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../components/UI/ToastContainer';
import DataGrid from '../../components/DataGrid';
import { ColumnDef } from '@tanstack/react-table';

const MeasureUnitsPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<{ status?: 'ACTIVE' | 'INACTIVE' }>({});
  const [sort, setSort] = useState<{ column: string; order: 'asc' | 'desc' }>({ column: 'name', order: 'asc' });
  const pageSize = 50;
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();

  const { column: sortBy, order: sortOrder } = sort;

  const { data, isLoading, error } = useQuery({
    queryKey: ['measure-units', currentPage, searchTerm, filters, sortBy, sortOrder],
    queryFn: () => measureUnitService.list({
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      status: filters.status,
      sortBy,
      sortOrder,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => measureUnitService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measure-units'] });
      setConfirmDelete(null);
      showSuccess('Measure unit deleted', 'The measure unit has been successfully deleted.');
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to delete measure unit. Please try again.';
      showError('Delete failed', message);
    },
  });

  const handleExport = useCallback(async () => {
    try {
      const blob = await measureUnitService.exportMeasureUnits({
        search: searchTerm,
        status: filters.status,
        ids: selectedIds.length ? selectedIds : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `measure-units-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    }
  }, [filters.status, searchTerm, selectedIds]);

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleBulkAction = useCallback(async (status: 'ACTIVE' | 'INACTIVE') => {
    if (!selectedIds.length) return;
    try {
      await measureUnitService.bulkUpdateStatus(selectedIds, status);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['measure-units'] });
    } catch (e) {
      console.error('Bulk update failed', e);
    }
  }, [queryClient, selectedIds]);

  const openCreate = useCallback(() => { setSelected(null); setIsModalOpen(true); }, []);
  const openEdit = useCallback((u: any) => { setSelected(u); setIsModalOpen(true); }, []);

  const units = data?.measureUnits || [];
  const pagination = data?.pagination;

  const columns = useMemo<ColumnDef<any>[]>(() => [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      meta: { sortable: true },
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'abbreviation',
      accessorKey: 'abbreviation',
      header: 'Abbreviation',
      meta: { sortable: true },
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'type',
      accessorKey: 'type',
      header: 'Type',
      meta: { sortable: true },
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      meta: { sortable: true },
      cell: ({ row }) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.original.status === 'ACTIVE'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => openEdit(row.original)}
            className="text-primary-600 hover:text-primary-900"
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setConfirmDelete(row.original)}
            className="text-red-600 hover:text-red-900"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ], [openEdit]);

  const handleSortChange = (columnId: string) => {
    setSort((prev) => {
      if (prev.column === columnId) {
        return { column: columnId, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { column: columnId, order: 'asc' };
    });
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">Error loading measure units.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Measure Units</h1>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={handleExport}>Export</Button>
          <Button onClick={openCreate}><PlusIcon className="w-5 h-5 mr-2" />Create</Button>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bg-white rounded-lg shadow-soft p-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">{selectedIds.length} selected</div>
          <div className="flex space-x-2">
            <Button onClick={() => handleBulkAction('ACTIVE')}>Activate</Button>
            <Button variant="danger" onClick={() => handleBulkAction('INACTIVE')}>Deactivate</Button>
            <Button variant="secondary" onClick={() => setSelectedIds([])}>Clear selection</Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search measure units..." 
              className="pl-10 form-input w-full" 
              value={searchTerm} 
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }} 
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft">
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-3" />
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                className="form-input w-full"
                value={filters.status || ''}
                onChange={(e) => {
                  setFilters({ status: (e.target.value as 'ACTIVE' | 'INACTIVE' | '') || undefined });
                  setCurrentPage(1);
                }}
              >
                <option value="">All</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <DataGrid
              data={units}
              columns={columns}
              getRowId={(row) => row.id}
              enableSelection
              selectedIds={selectedIds}
              onToggleRow={toggleRow}
              onToggleAll={(ids, checked) =>
                setSelectedIds((prev) =>
                  checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id))
                )
              }
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
          )}
        </div>

        {pagination && (
          <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * pageSize, pagination.total)}</span> of{' '}
              <span className="font-medium">{pagination.total}</span> results
            </p>
            <div className="space-x-2">
              <Button 
                variant="secondary" 
                disabled={currentPage === 1} 
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button 
                variant="secondary" 
                disabled={pagination && currentPage === pagination.pages} 
                onClick={() => setCurrentPage((p) => (pagination ? Math.min(pagination.pages, p + 1) : p + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <MeasureUnitModal
          measureUnit={selected}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['measure-units'] });
          }}
        />
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          title="Delete Measure Unit"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete.id)}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default MeasureUnitsPage;

